import React from 'react';
import { FaArrowDown, FaArrowRight } from 'react-icons/fa';
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FiCalendar, FiClock, FiUser, FiUsers, FiCreditCard, FiNavigation, FiFileText, FiShield } from 'react-icons/fi';

const GenderBadge = ({ gender }) => {
    if (!gender) return null;
    const isFemale = gender === "FEMALE";
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
            isFemale 
                ? "bg-pink-100 text-pink-700 border border-pink-200" 
                : "bg-amber-100 text-amber-800 border border-amber-200"
        }`}>
            {isFemale ? "♀ Female" : "♂ Male"}
        </span>
    );
};

const DesignationBadge = ({ email }) => {
    if (!email) return null;
    const isStudent = email.endsWith("@muj.manipal.edu");
    const isFaculty = email.endsWith("@jaipur.manipal.edu");
    if (!isStudent && !isFaculty) return null;
    return (
        <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${
            isFaculty 
                ? "bg-purple-100 text-purple-700 border border-purple-200" 
                : "bg-emerald-100 text-emerald-700 border border-emerald-200"
        }`}>
            {isFaculty ? "Faculty" : "Student"}
        </span>
    );
};

export const RideBar = ({ ride, action }) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const [date, time] = new Date(ride?.date).toISOString().slice(0, 16).split("T");
    const day = days[new Date(date).getDay()];

    const effectiveGenderPref = ride?.genderPreference || ride?.host?.genderPreference;
    const isFemaleOnly = ride?.host?.gender === "FEMALE" && effectiveGenderPref === "SAME";

    return (
        <Card className="mx-auto my-3 w-full max-w-3xl bg-white/95 backdrop-blur-md rounded-2xl border border-zinc-200/90 shadow-sm hover:shadow-md hover:border-amber-400/50 transition-all duration-200 p-4 sm:p-5">
            {/* Header: Locations & Badges */}
            <div className="flex flex-col gap-3.5">
                <div className="flex items-start justify-between gap-3">
                    {/* Route display: allows locations to wrap cleanly without truncating */}
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-zinc-900">
                            {/* Pickup */}
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-emerald-100 shrink-0 mt-1.5" />
                                <div className="min-w-0 flex-1">
                                    <span className="block text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Pickup</span>
                                    <span className="block font-bold text-base sm:text-lg text-zinc-900 leading-snug break-words" title={ride?.from}>
                                        {ride?.from}
                                    </span>
                                </div>
                            </div>

                            {/* Direction Arrow */}
                            <div className="hidden md:flex items-center text-amber-500 px-1 shrink-0 self-center">
                                <FaArrowRight className="w-4 h-4" />
                            </div>
                            <div className="flex md:hidden items-center text-amber-500 pl-4 py-0.5">
                                <FaArrowDown className="w-3.5 h-3.5" />
                            </div>

                            {/* Drop-off */}
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 ring-4 ring-amber-100 shrink-0 mt-1.5" />
                                <div className="min-w-0 flex-1">
                                    <span className="block text-[10px] font-bold text-amber-700 uppercase tracking-wider">Drop-off</span>
                                    <span className="block font-bold text-base sm:text-lg text-zinc-900 leading-snug break-words" title={ride?.to}>
                                        {ride?.to}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Female Only Badge */}
                    {isFemaleOnly && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-pink-50 text-pink-700 border border-pink-200 shrink-0 self-start shadow-xs">
                            <FiShield className="w-3.5 h-3.5 text-pink-500" /> Female Only
                        </span>
                    )}
                </div>

                {/* Ride details pills */}
                <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-zinc-700">
                    <span className="inline-flex items-center gap-1.5 bg-amber-50/80 border border-amber-200/70 px-2.5 py-1 rounded-lg text-amber-950 font-medium">
                        <FiCalendar className="w-3.5 h-3.5 text-amber-600" />
                        <span>{new Date(date).toLocaleDateString('en-GB')}</span>
                        <span className="text-xs text-amber-700 font-normal">({day})</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-zinc-100/80 border border-zinc-200/70 px-2.5 py-1 rounded-lg text-zinc-800 font-medium">
                        <FiClock className="w-3.5 h-3.5 text-amber-600" />
                        <span>{time}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-zinc-100/80 border border-zinc-200/70 px-2.5 py-1 rounded-lg text-zinc-800 font-medium">
                        <FiUsers className="w-3.5 h-3.5 text-zinc-500" />
                        <span>{ride?.capacity} seats</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-zinc-100/80 border border-zinc-200/70 px-2.5 py-1 rounded-lg text-zinc-800 font-medium">
                        <FiCreditCard className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="font-semibold">{ride?.totalFare ? `₹${ride.totalFare}` : "TBD"}</span>
                        {ride?.totalFare && <span className="text-[11px] text-zinc-500 font-normal">/seat</span>}
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-zinc-100/80 border border-zinc-200/70 px-2.5 py-1 rounded-lg text-zinc-800 font-medium">
                        <FiNavigation className="w-3.5 h-3.5 text-zinc-500" />
                        <span>{ride?.vehicleType?.charAt(0).toUpperCase() + ride?.vehicleType?.toLowerCase().slice(1)}</span>
                    </span>
                </div>

                {/* Description if available */}
                {ride?.description && (
                    <div className="text-xs sm:text-sm text-zinc-600 bg-zinc-50 border border-zinc-200/70 rounded-xl p-2.5 flex items-start gap-2">
                        <FiFileText className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                        <span className="leading-relaxed break-words">{ride.description}</span>
                    </div>
                )}
            </div>

            <Separator className="my-3.5 bg-zinc-200/80" />

            {/* Footer: Host Details & Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                {ride?.host && (
                    <div className="flex items-center gap-2.5 flex-wrap">
                        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0 font-medium">
                            <FiUser className="w-4 h-4" />
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs text-zinc-500 font-medium">Hosted by:</span>
                            <span className="text-sm font-bold text-zinc-900">{ride.host.name}</span>
                            <GenderBadge gender={ride.host?.gender} />
                            <DesignationBadge email={ride.host?.email} />
                        </div>
                    </div>
                )}

                {/* Inline Action Slot */}
                {action && (
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end sm:justify-start">
                        {action}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default RideBar;